/**
 * rambler.ru — stok ve site kullanım paneli (R-RAMBLER).
 *
 * DELIBERATELY NOT the shared MailcomPage template. That page's model is alias
 * inventory (mint 9 aliases per parent, renew, wipe); rambler has no aliases,
 * one unit per mailbox, and its scarce resource is the (mailbox, site) right.
 * Mounting the template showed numbers that do not answer the operator's
 * question.
 *
 * What this page answers:
 *   1. kaç stok var (toplam / satılabilir / kiralamada),
 *   2. stokların %kaçı hangi site için kullanıldı (kalıcı yanma),
 *   3. kaç (mailbox, site) hakkı kaldı ve kaç mailbox hiç yanmamış.
 *
 * The raw control-plane tools (import, accounts, readiness) stay reachable from
 * the link at the bottom — they are a tool, not the page.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  availableShare,
  fetchRamblerSiteUsage,
  type RamblerSiteUsage,
} from './siteUsage.js';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: RamblerSiteUsage }
  | { readonly kind: 'error'; readonly message: string };

export function RamblerPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });

  const load = useCallback(async (signal?: AbortSignal) => {
    setState({ kind: 'loading' });
    const res = await fetchRamblerSiteUsage(signal);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', data: res.data });
      return;
    }
    if (res.kind === 'auth_required') {
      setState({ kind: 'error', message: 'Oturum gerekli (admin girişi).' });
      return;
    }
    if (res.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Yetki yok: provider:inspect gerekli.' });
      return;
    }
    if (res.kind === 'network_error') {
      setState({ kind: 'error', message: `Bağlantı hatası: ${res.message}` });
      return;
    }
    setState({ kind: 'error', message: `İstek başarısız (${res.status}: ${res.reason}).` });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return (
    <section className="rambler-dashboard" data-testid="rambler-dashboard">
      <header className="rambler-dashboard__header">
        <div>
          <h1>rambler.ru — stok ve site kullanımı</h1>
          <p>
            Her satır bir mailbox (alias yok). Satış hakkı <strong>(mailbox, site)</strong>{' '}
            bazındadır: posta gelmezse yalnız o müşteriye kapanır, posta gelirse çift
            herkese yanar; mailbox başka site için satılabilir kalır.
          </p>
        </div>
        <button type="button" onClick={() => void load()} data-testid="rambler-refresh">
          Yenile
        </button>
      </header>

      {state.kind === 'loading' && <p data-testid="rambler-loading">Yükleniyor…</p>}
      {state.kind === 'error' && (
        <p role="alert" data-testid="rambler-error">
          {state.message}
        </p>
      )}

      {state.kind === 'ok' && (
        <>
          <div className="rambler-kpis">
            <Kpi label="Toplam stok" value={state.data.units.total} testId="kpi-total" />
            <Kpi
              label="Satılabilir"
              value={state.data.units.available}
              hint={`stokun %${availableShare(state.data.units)}`}
              testId="kpi-available"
            />
            <Kpi label="Kiralamada" value={state.data.units.leased} testId="kpi-leased" />
            <Kpi
              label="Yanmış (mailbox, site)"
              value={state.data.units.burnedPairs}
              testId="kpi-burned-pairs"
            />
            <Kpi
              label="Yanmış mailbox"
              value={state.data.units.mailboxesWithBurns}
              hint={`${state.data.units.total - state.data.units.mailboxesWithBurns} mailbox hiç yanmadı`}
              testId="kpi-burned-mailboxes"
            />
            <Kpi label="Hesap" value={state.data.accounts} testId="kpi-accounts" />
          </div>

          <h2>Site başına kullanım</h2>
          {state.data.sites.length === 0 ? (
            <p data-testid="rambler-no-sites">
              Henüz hiçbir site için yanma yok — tüm stok kullanılabilir.
            </p>
          ) : (
            <table data-testid="rambler-site-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Yanmış mailbox</th>
                  <th>Stok payı</th>
                  <th>Satıldı (yanmadı)</th>
                  <th>Son yanma</th>
                </tr>
              </thead>
              <tbody>
                {state.data.sites.map((row) => (
                  <tr key={row.site} data-testid={`site-row-${row.site}`}>
                    <td>{row.site}</td>
                    <td>{row.burnedMailboxes}</td>
                    <td>
                      <span className="rambler-bar" aria-hidden="true">
                        <span
                          className="rambler-bar__fill"
                          style={{ width: `${Math.min(100, row.shareOfStock)}%` }}
                        />
                      </span>
                      <span data-testid={`site-share-${row.site}`}>
                        %{row.shareOfStock}
                      </span>
                    </td>
                    <td>{row.servedMailboxes}</td>
                    <td>
                      {row.lastBurnedAt
                        ? new Date(row.lastBurnedAt).toLocaleString('tr-TR')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p className="rambler-footnote" data-testid="rambler-capacity-note">
            Kapasite: {state.data.units.total} mailbox × site başına 1 satış hakkı.
            Şu an satılabilir {state.data.units.available} mailbox; yanmış çiftler
            yalnızca kendi sitesi için kapalıdır.
          </p>
        </>
      )}

      <footer className="rambler-dashboard__footer">
        <a href="/rambler-control-plane">
          Ham kontrol düzlemi (import, hesaplar, readiness) →
        </a>
      </footer>
    </section>
  );
}

function Kpi(props: {
  readonly label: string;
  readonly value: number;
  readonly hint?: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div className="rambler-kpi" data-testid={props.testId}>
      <span className="rambler-kpi__label">{props.label}</span>
      <span className="rambler-kpi__value">{props.value}</span>
      {props.hint ? <span className="rambler-kpi__hint">{props.hint}</span> : null}
    </div>
  );
}
